/**
 * Which friends' meals Compras adds to my list, remembered per viewer in
 * this browser. A friend I've planned for (opened in friend mode) starts
 * included; any explicit toggle in Compras wins from then on, including an
 * explicit "off" over a later visit to their weeks.
 */

/** friendId → included. An absent key means "never decided". */
export type ComprasFriendPrefs = Record<number, boolean>;

type StorageLike = Pick<Storage, "getItem" | "setItem">;

export function comprasFriendsKey(userId: number): string {
  return `nutri:compras-friends:${userId}`;
}

/** The accepted friends that are switched on. Ended friendships drop out. */
export function includedFriendIds(prefs: ComprasFriendPrefs, acceptedIds: readonly number[]): Set<number> {
  return new Set(acceptedIds.filter((id) => prefs[id] === true));
}

/** Include a friend I plan for, unless I already decided either way. */
export function markPlannedFor(prefs: ComprasFriendPrefs, friendId: number): ComprasFriendPrefs {
  if (Object.hasOwn(prefs, friendId)) return prefs;
  return { ...prefs, [friendId]: true };
}

export function setIncluded(prefs: ComprasFriendPrefs, friendId: number, on: boolean): ComprasFriendPrefs {
  if (prefs[friendId] === on) return prefs;
  return { ...prefs, [friendId]: on };
}

/** Keeps only `{ "<positive int>": boolean }` entries; anything else reads as empty. */
export function parseComprasFriendPrefs(raw: string | null): ComprasFriendPrefs {
  if (!raw) return {};
  try {
    const data: unknown = JSON.parse(raw);
    if (typeof data !== "object" || data === null || Array.isArray(data)) return {};
    const prefs: ComprasFriendPrefs = {};
    for (const [key, value] of Object.entries(data)) {
      if (/^[1-9]\d*$/.test(key) && typeof value === "boolean") prefs[Number(key)] = value;
    }
    return prefs;
  } catch {
    return {};
  }
}

// Storage can be missing or throw (private mode, blocked site data, quota):
// Compras then simply starts with nobody included.

export function loadComprasFriendPrefs(userId: number, storage?: StorageLike): ComprasFriendPrefs {
  try {
    return parseComprasFriendPrefs((storage ?? globalThis.localStorage).getItem(comprasFriendsKey(userId)));
  } catch {
    return {};
  }
}

export function saveComprasFriendPrefs(userId: number, prefs: ComprasFriendPrefs, storage?: StorageLike): void {
  try {
    (storage ?? globalThis.localStorage).setItem(comprasFriendsKey(userId), JSON.stringify(prefs));
  } catch {}
}

/** Friend mode calls this on entry: the friend starts included in my Compras. */
export function rememberPlannedFor(userId: number, friendId: number, storage?: StorageLike): void {
  const prefs = loadComprasFriendPrefs(userId, storage);
  const next = markPlannedFor(prefs, friendId);
  if (next !== prefs) saveComprasFriendPrefs(userId, next, storage);
}
